import { useMemo } from "react";
import { t } from "@/i18n-js/instance";
import { useFetchProration } from "@/react/components/shared/UpgradePlan/hooks/useFetchProration";
import type { Plan } from "@circle-react/types";
import { I18N_LOCALE_ROOT } from "../constants";

export interface CalculatedPricing {
  annualSavings?: string;
  currentPlanCredit?: string;
  discountCoupon?: string;
  dueToday?: string;
  isAnnualPlan?: boolean;
}

interface ProrationData {
  display_prorated_charges_credit_amount?: string;
  display_total_discount?: string;
  display_total_due_today?: string;
}

interface UsePlanPricingProps {
  alternativePlan?: Plan;
  isAnnualPlan: boolean;
  isCommunityOnTrial: boolean;
  isPlanLoading: boolean;
  plan?: Plan;
  planCouponId?: string;
}

const extractProrationPricing = (
  prorationData: ProrationData,
): CalculatedPricing => {
  const result: CalculatedPricing = {};

  if (prorationData.display_prorated_charges_credit_amount) {
    result.currentPlanCredit =
      prorationData.display_prorated_charges_credit_amount;
  }

  if (prorationData.display_total_discount) {
    result.discountCoupon = prorationData.display_total_discount;
  }

  if (prorationData.display_total_due_today) {
    result.dueToday = prorationData.display_total_due_today;
  }

  return result;
};

const calculateDueTodayAmount = (plan: Plan, isAnnualPlan: boolean): string => {
  if (plan.amount && typeof plan.amount === "number") {
    const currency = plan.currency?.toUpperCase() || "USD";
    return `$${(plan.amount / 100).toFixed(0)} ${currency}`;
  }

  if (isAnnualPlan) {
    return plan.annual_base_amount_with_currency || "";
  }

  return plan.display_base_monthly_amount || "";
};

export const usePlanPricing = ({
  plan,
  alternativePlan,
  isAnnualPlan,
  isCommunityOnTrial,
  planCouponId,
  isPlanLoading,
}: UsePlanPricingProps) => {
  // Only fetch proration data if the community is not on trial
  const shouldFetchProration = !isCommunityOnTrial && !!plan?.id;

  const { prorationData, isProrationLoading, errorFetchingProration } =
    useFetchProration({
      new_plan_id: plan?.id || 0,
      plan_coupon_id: planCouponId,
      only_ch: true,
      enabled: shouldFetchProration,
    });

  const pricing = useMemo((): CalculatedPricing => {
    if (!plan) return {};

    let calculatedPricing: CalculatedPricing = {};

    // Use proration data if available
    if (prorationData && !errorFetchingProration) {
      calculatedPricing = extractProrationPricing(prorationData);
    }

    // Always include annual savings (regardless of interval)
    // Use the annual plan's discount amount if we're on annual, otherwise use the alternative plan's discount
    if (!calculatedPricing.annualSavings) {
      const annualPlan = isAnnualPlan ? plan : alternativePlan;
      if (annualPlan?.annual_discount_amount_with_currency) {
        const currency = plan.currency.toUpperCase();
        calculatedPricing.annualSavings = t(
          `${I18N_LOCALE_ROOT}.pricing_summary.discount_format`,
          {
            amount: annualPlan.annual_discount_amount_with_currency,
            currency: currency,
          },
        );
      }
    }

    // Calculate due today if not set by proration
    if (!calculatedPricing.dueToday) {
      calculatedPricing.dueToday = calculateDueTodayAmount(plan, isAnnualPlan);
    }

    // Add the isAnnualPlan flag to the pricing object
    calculatedPricing.isAnnualPlan = isAnnualPlan;

    return calculatedPricing;
  }, [
    plan,
    alternativePlan,
    isAnnualPlan,
    prorationData,
    errorFetchingProration,
  ]);

  // Only consider proration loading if we're actually fetching proration
  const isLoadingPricing = !!(
    isPlanLoading ||
    (shouldFetchProration && isProrationLoading)
  );

  return {
    pricing,
    isLoadingPricing,
  };
};
